import '../../../ProductCard/ProductCard.scss';
import './Wrapper.scss';

import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { listerClickThrough } from '../../../../actions/lister-actions';
import { useIsProductInHighDemand } from '../../../../hooks/useIsProductInHighDemand';
import AddToBasketButton from '../../../ProductCard/AddToBasket/AddToBasketButton';
import BackInStockButton from '../../../ProductCard/BackInStockButton';
import EnergyInformation from '../../../ProductCard/EnergyInformation/EnergyInformation';
import FinanceProposition from '../../../ProductCard/FinanceProposition';
import Heading from '../../../ProductCard/Heading';
import HighDemandMessaging from '../../../ProductCard/HighDemand/HighDemandMessaging';
import MoreInfo from '../../../ProductCard/MoreInfo';
import ProductImage from '../../../ProductCard/ProductImage/ProductImage';
import ReviewRating from '../../../ProductCard/ReviewRating/ReviewRating';
import StockStatus from '../../../ProductCard/StockStatus';
import { Context } from '../../store/store';
import LargeListViewSkeleton from '../ListViewContent/Skeletons/LargeListViewSkeleton';
import PricePodV2 from '../../../ProductCard/PricePod/V2/PricePodV2';
import loadable from '@loadable/component';

const RecommendationCarousel = loadable(() =>
	import('../../../ProductCard/RecommendationCarousel/RecommendationCarousel')
);

const CardContentEnhanced = ({
	index,
	product,
	tradingRangeInfo,
	isFeatured,
}) => {
	const { ListerAlternativeProductsEnabled } = useSelector(
		(state) => state.experimentSwitches
	);

	const [contextState] = useContext(Context);
	const { loaded } = contextState;

	const dispatch = useDispatch();
	const isSkuHighDemand = useIsProductInHighDemand(product);

	if (!product) {
		return null;
	}

	if (!loaded) {
		return <LargeListViewSkeleton />;
	}

	return (
		<>
			<div className="bg-ongreen p-2 text-center font-headline text-white rounded-md">
				Enhanced
			</div>

			<div className="flex flex-wrap">
				<div className="flex flex-col p-2 w-1/3 md:w-1/4 z-10">
					<ProductImage {...product} />
				</div>

				<div
					className="w-2/3 md:w-1/2 p-2 z-10"
					style={{ height: 'fit-content' }}>
					<a
						href={product.FullProductUrl}
						onClick={(event) => {
							event.preventDefault();
							window.location.assign(product.FullProductUrl);

							dispatch(listerClickThrough(product.Code));
						}}
						className="inline-block"
						data-testid="product-card-link">
						<Heading content={product.FullTitle} />
					</a>

					<ReviewRating {...product} />
					<div className="border border-gray-30 border-solid h-16 my-4 rounded hidden md:block"></div>

					<div className="md:hidden">
						<PricePodV2
							product={product}
							viewModel={product.PricePodViewModel}
							inStock={product.State !== 'OutOfStock'}
							{...product}
						/>

						{product.State !== 'OutOfStock' ? (
							<div className="z-10">
								{isSkuHighDemand && <HighDemandMessaging classes="my-4" />}

								{!isSkuHighDemand && (
									<AddToBasketButton classes="mt-2" {...product} />
								)}
							</div>
						) : (
							<div className="z-10">
								<StockStatus
									stockStatus={product.State}
									productCode={product.Code}
								/>
								<BackInStockButton {...product} classes="mt-2 z-10" />
							</div>
						)}
					</div>
				</div>

				<div className="hidden md:flex flex-col justify-start p-2 w-1/4 lg:justify-between">
					<EnergyInformation
						classes="text-center mt-4"
						energyRatingDetails={product.EnergyRatingDetails}
					/>
					{product && product?.State !== 'OutOfStock' && (
						<FinanceProposition {...product} />
					)}
					<div>
						<PricePodV2
							product={product}
							viewModel={product.PricePodViewModel}
							inStock={product.State !== 'OutOfStock'}
							{...product}
						/>
					</div>

					{product.State !== 'OutOfStock' ? (
						<div className="z-10">
							{isSkuHighDemand && <HighDemandMessaging classes="my-4" />}

							{!isSkuHighDemand && (
								<AddToBasketButton classes="mt-2" {...product} />
							)}
							<MoreInfo {...product} classes="mt-2" />
						</div>
					) : (
						<div className="z-10">
							<StockStatus
								stockStatus={product.State}
								productCode={product.Code}
							/>
							<BackInStockButton {...product} classes="mt-2 z-10" />
							<MoreInfo {...product} classes="mt-2" />
						</div>
					)}
				</div>
			</div>

			{ListerAlternativeProductsEnabled && product.State === 'OutOfStock' && (
				<RecommendationCarousel NowPrice={product.PricePodViewModel.NowPrice} />
			)}
		</>
	);
};

export default CardContentEnhanced;
